import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";
import { scrollToId } from "@/lib/lenis";

const QUICK = [
  "Cela soulage-t-il la sciatique ?",
  "Livraison en combien de temps ?",
  "Quels sont les prix des packs ?",
  "Compatible siège auto ?",
];

const WELCOME =
  "Bonjour ! Je suis Claire, l'assistante Ergocox™. Douleurs au coccyx, sciatique, livraison, prix ? Posez-moi toutes vos questions.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())));
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streaming, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const evt = JSON.parse(p.slice(6));
          if (evt.type === "delta" || evt.type === "error") {
            setMessages((m) => {
              const c = [...m];
              const last = c[c.length - 1];
              c[c.length - 1] = { ...last, content: last.content + evt.content };
              return c;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        const last = c[c.length - 1];
        c[c.length - 1] = { ...last, content: (last.content || "") + "Oups, une erreur est survenue. Réessayez dans un instant." };
        return c;
      });
    }
    setStreaming(false);
  };

  const goOffers = () => {
    setOpen(false);
    setTimeout(() => scrollToId("offres"), 200);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-[75] w-[calc(100vw-2rem)] max-w-[380px] h-[560px] max-h-[70vh] flex flex-col rounded-3xl bg-cream border border-sage/15 shadow-2xl shadow-sage/30 overflow-hidden"
          >
            <div className="flex items-center justify-between gap-3 px-5 py-4 bg-sage text-cream">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full bg-cream/15 grid place-items-center font-serif italic text-lg">C</span>
                <div>
                  <p className="text-sm font-semibold leading-tight">Claire · Assistante Ergocox™</p>
                  <p className="text-[11px] text-cream/70 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 inline-block" /> En ligne — répond en français
                  </p>
                </div>
              </div>
              <button
                data-testid="chat-close-button"
                onClick={() => setOpen(false)}
                aria-label="Fermer le chat"
                className="p-1.5 rounded-full hover:bg-cream/10 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "self-end max-w-[85%]" : "self-start max-w-[90%]"}>
                  <div
                    data-testid={`chat-message-${m.role}-${i}`}
                    className={
                      m.role === "user"
                        ? "rounded-2xl rounded-br-md bg-sage text-cream px-4 py-2.5 text-sm leading-relaxed"
                        : "rounded-2xl rounded-bl-md bg-white border border-sage/15 px-4 py-2.5 text-sm leading-relaxed text-ink whitespace-pre-wrap"
                    }
                  >
                    {(m.content || "").replace(/\*\*/g, "").replace(/^#+\s/gm, "") || (
                      <span className="flex gap-1 py-1" data-testid="chat-typing-indicator">
                        <span className="w-1.5 h-1.5 rounded-full bg-sage/50 animate-bounce" />
                        <span className="w-1.5 h-1.5 rounded-full bg-sage/50 animate-bounce" style={{ animationDelay: "0.15s" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-sage/50 animate-bounce" style={{ animationDelay: "0.3s" }} />
                      </span>
                    )}
                  </div>
                  {m.role === "assistant" && m.content && /€|pack/i.test(m.content) && (
                    <button
                      data-testid={`chat-cta-offers-${i}`}
                      onClick={goOffers}
                      className="mt-2 inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra/90 text-white text-xs font-semibold px-4 py-2 transition-all hover:scale-[1.03]"
                    >
                      Voir les offres <ArrowRight size={13} />
                    </button>
                  )}
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="chat-quick-replies">
                  {QUICK.map((q, i) => (
                    <button
                      key={i}
                      data-testid={`chat-quick-reply-${i}`}
                      onClick={() => send(q)}
                      className="rounded-full border border-sage/25 bg-white px-3.5 py-2 text-xs font-medium text-sage hover:bg-sage-soft transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-sage/10 bg-white px-3 py-3"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Écrivez votre question…"
                className="flex-1 rounded-full border border-sage/20 bg-cream px-4 py-2.5 text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20"
              />
              <button
                data-testid="chat-send-button"
                type="submit"
                disabled={streaming || !input.trim()}
                aria-label="Envoyer"
                className="w-10 h-10 shrink-0 rounded-full bg-sage hover:bg-sage-hover text-cream grid place-items-center transition-all hover:scale-105 disabled:opacity-40"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button
        data-testid="chat-open-button"
        onClick={() => setOpen(!open)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 260, damping: 20 }}
        whileHover={{ scale: 1.06 }}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[55] w-14 h-14 rounded-full bg-sage hover:bg-sage-hover text-cream shadow-xl shadow-sage/30 grid place-items-center"
        aria-label="Ouvrir l'assistance"
      >
        <MessageCircle size={24} />
        {!open && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-terra border-2 border-cream" data-testid="chat-notification-dot" />}
      </motion.button>
    </>
  );
}
